"use client"

import React, { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { ethers } from "ethers"
import { Loader2 } from "lucide-react"
import NFTOTEM_ABI from "@/lib/abi/nftotem"
import { useToast } from "@/hooks/use-toast"
import { ToastAction } from "@/components/ui/toast"
import { getOpenSeaUrl } from "@/lib/utils"
import { getCurrentNetwork } from "@/lib/ethereum-config"
import { mintingStore } from "@/lib/minting-store"
import { analytics } from "@/lib/analytics"
import { buildAuthMessage } from '@/lib/auth-message'
import { getCubeImageForSnapshot } from '@/lib/seasonal-images'

interface MetaMaskButtonProps {
  walletAddress: string;
  connectMetaMask: () => void;
  provider: any;
  address: string | null;
  type: string | null;
  connecting: boolean;
  error: string | null;
  className?: string;
  currentCollections: { id: string; name: string; image: string }[];
  tokenId?: number | null;
  onError?: (error: string) => void; // Callback for passing errors to parent component
}

const CONTRACT_ADDRESS = getCurrentNetwork().contractAddress;
const DEFAULT_PRICE = '0.003';

// Robust chain detection helper
async function getConnectedChainId(provider: any, ethersProvider: ethers.BrowserProvider): Promise<number | null> {
  try {
    const chainIdHex = await provider?.request?.({ method: 'eth_chainId' });
    if (typeof chainIdHex === 'string') {
      const parsed = parseInt(chainIdHex, 16);
      if (!Number.isNaN(parsed)) return parsed;
    }
  } catch {}
  try {
    const network = await ethersProvider.getNetwork();
    return Number(network.chainId);
  } catch {}
  return null;
}

// Reliable data persistence with retry mechanism
async function persistTotemDataWithRetry(
  walletAddress: string, 
  tokenId: number, 
  currentCollections: { id: string; name: string; image: string }[]
): Promise<void> {
  const maxRetries = 3;
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`[MetaMaskButton] Persisting totem data (attempt ${attempt + 1}/${maxRetries})`);
      
      // Generate image first
      let imageUrl = '';
      try {
        const imageRes = await fetch('/api/generate-totem-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            walletAddress, 
            collections: currentCollections.map(c => c.id), 
            tokenId,
            isEdit: false // Indicates this is minting, not editing
          })
        });
        
        if (imageRes.ok) {
          const imageData = await imageRes.json();
          imageUrl = imageData?.data?.metadata?.image || '';
          console.log(`[MetaMaskButton] Generated image URL: ${imageUrl}`);
        }
      } catch (imageError) {
        console.warn('[MetaMaskButton] Image generation failed, continuing without image:', imageError);
      }
      
      // Save ownership data with signature
      const issuedAt = Date.now();
      const authMsg = buildAuthMessage({ action: 'create-ownership', walletAddress, issuedAt, tokenId });
      // signer derived later in calling scope, so sign here via window.ethereum if available
      let signature: string | null = null;
      try {
        // Best-effort use of injected provider
        // @ts-ignore
        const injected = (globalThis as any).ethereum;
        if (injected) {
          const ethersProvider = new ethers.BrowserProvider(injected);
          const signer = await ethersProvider.getSigner();
          signature = await signer.signMessage(authMsg);
        }
      } catch {}

      const saveRes = await fetch('/api/create-totem-ownership', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walletAddress,
          tokenId,
          collections: currentCollections.map(c => ({
            id: c.id,
            name: c.name,
            cubeImage: getCubeImageForSnapshot(c.id) // Get seasonal image if active, otherwise default
          })),
          blockCount: currentCollections.length,
          imageUrl: imageUrl,
          signature,
          issuedAt
        }),
      });
      
      if (!saveRes.ok) {
        const errorData = await saveRes.text();
        throw new Error(`Save failed: ${saveRes.status} ${saveRes.statusText} - ${errorData}`);
      }
      
      const saveData = await saveRes.json();
      if (!saveData.success) {
        throw new Error(`Save API returned error: ${saveData.error}`);
      }
      
      console.log('[MetaMaskButton] Totem ownership saved successfully');
      
      // Verify the data was actually saved
      try {
        const verifyRes = await fetch(`/api/recover-kv-data?wallet=${walletAddress}`);
        const verifyData = await verifyRes.json();
        
        if (verifyData.success && verifyData.data.exists) {
          console.log('[MetaMaskButton] KV data verification successful');
          return; // Success!
        } else {
          throw new Error('KV data verification failed - data not found after save');
        }
      } catch (verifyError) {
        console.warn('[MetaMaskButton] KV verification failed, but save appeared successful:', verifyError);
        return; // Consider it successful if save API succeeded
      }
      
    } catch (error) {
      lastError = error as Error;
      console.error(`[MetaMaskButton] Attempt ${attempt + 1} failed:`, error);
      
      if (attempt < maxRetries - 1) {
        const delay = 1000 * Math.pow(2, attempt); // Exponential backoff
        console.log(`[MetaMaskButton] Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // If we get here, all retries failed
  console.error('[MetaMaskButton] All persistence attempts failed:', lastError);
  
  // Try to recover the data using the recovery API
  try {
    const recoverRes = await fetch('/api/recover-kv-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        walletAddress,
        tokenId,
        collections: currentCollections.map(c => ({
          id: c.id,
          name: c.name,
          cubeImage: getCubeImageForSnapshot(c.id)
        })),
        blockCount: currentCollections.length,
        forceRecover: true
      })
    });
    
    if (recoverRes.ok) {
      const recoverData = await recoverRes.json();
      if (recoverData.success) {
        console.log('[MetaMaskButton] Data recovered successfully via recovery API');
        return;
      }
    }
  } catch (recoverError) {
    console.error('[MetaMaskButton] Recovery API also failed:', recoverError);
  }
  
  // If everything fails, show a warning but don't block the user
  console.warn('[MetaMaskButton] CRITICAL: Could not persist totem data. User should contact support.');
}

// Helper to truncate an Ethereum address to 0x1234…6789
function formatAddressForUi(address: string, visibleChars: number = 4): string {
  if (!address || address.length <= 2 + visibleChars * 2) return address;
  const prefix = address.slice(0, 2 + visibleChars);
  const suffix = address.slice(-visibleChars);
  return `${prefix}…${suffix}`;
}

// Debug logging only in development
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[MetaMaskButton]', ...args)
  }
}

const debugWarn = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn('[MetaMaskButton]', ...args)
  }
}

const debugError = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.error('[MetaMaskButton]', ...args)
  }
}

  // Check contract address when component loads
debugLog('[MetaMaskButton] Environment CONTRACT_ADDRESS:', process.env.NEXT_PUBLIC_CONTRACT_ADDRESS_SEPOLIA_NEW);
debugLog('[MetaMaskButton] Using CONTRACT_ADDRESS:', CONTRACT_ADDRESS);
debugLog('[MetaMaskButton] CONTRACT_ADDRESS constant value:', CONTRACT_ADDRESS);
debugLog('[MetaMaskButton] Type of CONTRACT_ADDRESS:', typeof CONTRACT_ADDRESS);

// Removed contract address writes to localStorage to avoid stale cache and mismatches

export default function MetaMaskButton({ walletAddress, connectMetaMask, provider, address, type, connecting, error, className, currentCollections, tokenId, onError }: MetaMaskButtonProps) {
  const [hasMinted, setHasMinted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mintPrice, setMintPrice] = useState<string | null>(null);
  const [isFreeMint, setIsFreeMint] = useState<boolean | null>(null);
  const [metaMaskAvailable, setMetaMaskAvailable] = useState<boolean>(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isWrongNetwork, setIsWrongNetwork] = useState<boolean>(false);
  const [isWrongWallet, setIsWrongWallet] = useState<boolean>(false);
  const [showReconnectPrompt, setShowReconnectPrompt] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState(false); // Protection against duplication
  const [apiError, setApiError] = useState<string | null>(null);
  const [attemptedAutoSwitch, setAttemptedAutoSwitch] = useState<boolean>(false);
  const [autoSwitchFailed, setAutoSwitchFailed] = useState<boolean>(false);
  const { toast } = useToast();
  
  // Treat existing tokenId as source of truth for minted state
  const userHasMinted = hasMinted || (typeof tokenId === 'number' && tokenId > 0);
  
  // Detect wrong network and keep state in sync with wallet
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    const updateNetworkStatus = async () => {
      try {
        if (!provider) { setIsWrongNetwork(false); return; }
        const ethersProvider = new ethers.BrowserProvider(provider);
        const currentChainId = await getConnectedChainId(provider, ethersProvider);
        const expectedChainId = getCurrentNetwork().chainId;
        setIsWrongNetwork(currentChainId !== expectedChainId);
      } catch {
        // If we cannot detect, do not block the user
        setIsWrongNetwork(false);
      }
    };
    updateNetworkStatus();
    if (provider?.on) {
      const handler = () => { updateNetworkStatus(); };
      provider.on('chainChanged', handler);
      unsubscribe = () => provider.removeListener?.('chainChanged', handler);
    }
    return () => { if (unsubscribe) unsubscribe(); };
  }, [provider, address, type]);

  // Auto-switch to the expected network once when mismatch is detected
  useEffect(() => {
    const autoSwitch = async () => {
      if (!provider || !isWrongNetwork || attemptedAutoSwitch) return;
      setAttemptedAutoSwitch(true);
      setAutoSwitchFailed(false);
      try {
        const currentNetwork = getCurrentNetwork();
        const chainId = `0x${currentNetwork.chainId.toString(16)}`;
        await provider.request?.({ method: 'wallet_switchEthereumChain', params: [{ chainId }] });
        setIsWrongNetwork(false);
        setAutoSwitchFailed(false);
      } catch (switchError: any) {
        if (switchError?.code === 4902 || /Unrecognized chain ID|not added/i.test(String(switchError?.message))) {
          try {
            const currentNetwork = getCurrentNetwork();
            const chainId = `0x${currentNetwork.chainId.toString(16)}`;
            const networkParams = currentNetwork.chainId === 1
              ? {
                  chainId,
                  chainName: 'Ethereum Mainnet',
                  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
                  rpcUrls: [currentNetwork.rpcUrl || 'https://eth-mainnet.g.alchemy.com/v2/demo'],
                  blockExplorerUrls: ['https://etherscan.io']
                }
              : {
                  chainId,
                  chainName: 'Sepolia',
                  nativeCurrency: { name: 'Sepolia Ether', symbol: 'SEP', decimals: 18 },
                  rpcUrls: [currentNetwork.rpcUrl || 'https://rpc.sepolia.org'],
                  blockExplorerUrls: ['https://sepolia.etherscan.io']
                };
            await provider.request?.({ method: 'wallet_addEthereumChain', params: [networkParams] });
            setIsWrongNetwork(false);
            setAutoSwitchFailed(false);
          } catch (addError) {
            setAutoSwitchFailed(true);
          }
        } else {
          setAutoSwitchFailed(true);
        }
      }
    };
    autoSwitch();
  }, [provider, isWrongNetwork, attemptedAutoSwitch]);
  
  // Use ref to track collections and prevent infinite loops
  const collectionsRef = useRef<string>('');
  const lastCheckRef = useRef<number>(0);
  const wrongWalletTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Ask MetaMask to switch/select account when user needs to reconnect
  const handleConnectOrSwitch = async () => {
    if (!metaMaskAvailable) return;
    try {
      if (provider && isWrongWallet) {
        // Prompt the account switch UI
        await provider.request?.({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }]
        });
        // Fallback in case some wallets ignore wallet_requestPermissions
        await provider.request?.({ method: 'eth_requestAccounts' });
      } else {
        connectMetaMask?.();
      }
    } catch (err) {
      debugWarn('[MetaMaskButton] Account switch/connect failed:', err);
    }
  };

  // Check for MetaMask presence on every render
  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).ethereum) {
      setMetaMaskAvailable(true);
    } else {
      setMetaMaskAvailable(false);
    }
  }, []);

  // Check minting status and eligibility
  useEffect(() => {
    // Reset connecting state when wallet is successfully connected
    if (address && type === 'metamask' && connecting) {
      // Wallet is connected, reset connecting state
      debugLog('[MetaMaskButton] Wallet connected, resetting connecting state');
      // Force a re-render by updating local state
      setIsLoading(false);
      setIsCheckingStatus(false);
    }

    // Early check: connected wallet vs target wallet
    if (address && walletAddress) {
      const mismatch = address.toLowerCase() !== walletAddress.toLowerCase();
      setIsWrongWallet(mismatch);
      if (mismatch) {
        const truncated = formatAddressForUi(walletAddress);
        onError?.(`You can’t mint a totem for another wallet. Connect wallet ${truncated} in MetaMask or change the target wallet on the page.`);
        setShowReconnectPrompt(false);
        if (wrongWalletTimerRef.current) clearTimeout(wrongWalletTimerRef.current);
        wrongWalletTimerRef.current = setTimeout(() => {
          setShowReconnectPrompt(true);
          onError?.('');
        }, 5000);
      } else {
        // Clear previous error if any
        onError?.('');
        setShowReconnectPrompt(false);
        if (wrongWalletTimerRef.current) {
          clearTimeout(wrongWalletTimerRef.current);
          wrongWalletTimerRef.current = null;
        }
      }
    } else {
      // No connected address yet → do not block by wallet mismatch
      setIsWrongWallet(false);
      setShowReconnectPrompt(false);
      if (wrongWalletTimerRef.current) {
        clearTimeout(wrongWalletTimerRef.current);
        wrongWalletTimerRef.current = null;
      }
    }

    const checkMintStatus = async () => {
      if (address && type === 'metamask' && !isCheckingStatus) {
        // Create a hash of collections to detect changes
        const collectionsHash = currentCollections.map(c => c.id).sort().join(',');
        
        // Only check if collections have changed or if we haven't checked recently
        const now = Date.now();
        if (collectionsHash === collectionsRef.current && now - lastCheckRef.current < 5000) {
          return; // Skip if collections haven't changed and we checked recently
        }
        
        collectionsRef.current = collectionsHash;
        lastCheckRef.current = now;
        
        setIsCheckingStatus(true);
        try {
          // Pass collections for eligibility check
          const collectionsToSend = currentCollections.map(c => c.id);
          debugLog('[MetaMaskButton] Checking mint status with collections:', collectionsToSend);
          
          // Check if there are collections for free mint
          const FREE_MINT_COLLECTION_IDS = ['ilya', 'neko-darumas', 'ftcg'];
          const hasFreeMintCollections = collectionsToSend.some(c => FREE_MINT_COLLECTION_IDS.includes(c));
          debugLog('[MetaMaskButton] Has free mint collections:', hasFreeMintCollections, 'Free collections found:', collectionsToSend.filter(c => FREE_MINT_COLLECTION_IDS.includes(c)));
          
          const res = await fetch('/api/mint-totem', {
          method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ walletAddress, collections: collectionsToSend }),
          });
          const data = await res.json();
          debugLog('[MetaMaskButton] API response:', data);
          
          if (data && data.success) {
            setMintPrice(data.data?.price ?? DEFAULT_PRICE);
            setIsFreeMint(Boolean(data.data?.isFreeMint));
            setHasMinted(false); // default false, will clarify below
            debugLog('[MetaMaskButton] Set isFreeMint:', data.data.isFreeMint, 'price:', data.data.price);
          // Note: hasMinted check is now handled by frontend state management
          // API no longer returns this error
          } else if (data && data.collectionsError) {
            // Handle collections fetching error
            console.error('[MetaMaskButton] Collections error:', data.error);
            setApiError(data.error);
            setMintPrice(DEFAULT_PRICE);
            setIsFreeMint(false);
            setHasMinted(false);
          } else if (data && data.noCollections) {
            // Handle no collections error
            console.error('[MetaMaskButton] No collections error:', data.error);
            setApiError(data.error);
            setMintPrice(DEFAULT_PRICE);
            setIsFreeMint(false);
            setHasMinted(false);
          } else {
            setMintPrice(DEFAULT_PRICE);
            setIsFreeMint(false);
            setHasMinted(false);
            debugLog('[MetaMaskButton] Default values set');
          }
          // On-chain minted check removed from status phase to avoid UI delays.
        } catch (e) {
          debugError('[MetaMaskButton] Error checking mint status:', e);
          setMintPrice(DEFAULT_PRICE);
          setIsFreeMint(false);
          setHasMinted(false);
        } finally {
          setIsCheckingStatus(false);
        }
      }
    };
    checkMintStatus();
  }, [address, type, walletAddress, provider]); // Removed currentCollections from dependencies

  const handleMintOrEdit = async () => {
    debugLog('[MetaMaskButton] handleMintOrEdit called!');
    debugLog('[MetaMaskButton] CONTRACT_ADDRESS in handleMintOrEdit:', CONTRACT_ADDRESS);
    debugLog('[MetaMaskButton] CONTRACT_ADDRESS === expected:', CONTRACT_ADDRESS === '0x2f7ba9643e8083152df22e0a477c7112d532837d');
    
    // Prevent duplicate requests
    if (isLoading || isProcessing) {
      debugLog('[MetaMaskButton] Request already in progress, skipping...');
      return;
    }

    // If user is on wrong network, suggest switching
  if (isWrongNetwork) {
      try {
        const currentNetwork = getCurrentNetwork();
        const chainId = `0x${currentNetwork.chainId.toString(16)}`;
        await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId }] });
      } catch (switchError: any) {
        if (switchError?.code === 4902 || /Unrecognized chain ID|not added/i.test(String(switchError?.message))) {
          try {
            const currentNetwork = getCurrentNetwork();
            const chainId = `0x${currentNetwork.chainId.toString(16)}`;
            
            // Define network parameters based on current network
            let networkParams;
            if (currentNetwork.chainId === 1) {
              // Mainnet
              networkParams = {
                chainId,
                chainName: 'Ethereum Mainnet',
                nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
                rpcUrls: [currentNetwork.rpcUrl || 'https://eth-mainnet.g.alchemy.com/v2/demo'],
                blockExplorerUrls: ['https://etherscan.io']
              };
            } else {
              // Sepolia
              networkParams = {
                chainId,
                chainName: 'Sepolia',
                nativeCurrency: { name: 'Sepolia Ether', symbol: 'SEP', decimals: 18 },
                rpcUrls: [currentNetwork.rpcUrl || 'https://rpc.sepolia.org'],
                blockExplorerUrls: ['https://sepolia.etherscan.io']
              };
            }
            
            await provider.request({
              method: 'wallet_addEthereumChain',
              params: [networkParams],
            });
          } catch (addError) {
            debugError('[MetaMaskButton] Error adding network:', addError);
            onError?.(`Failed to add ${getCurrentNetwork().name} network. Please add it manually.`);
          }
        } else {
          debugError('[MetaMaskButton] Error switching network:', switchError);
          onError?.(`Failed to switch to ${getCurrentNetwork().name} network.`);
        }
      }
      return;
    }

    // Check that contract exists on current network
    try {
      const ethersProvider = new ethers.BrowserProvider(provider);
      const currentChainId = await getConnectedChainId(provider, ethersProvider);
      
      debugLog('[MetaMaskButton] Checking contract at address:', CONTRACT_ADDRESS);
      debugLog('[MetaMaskButton] Current network chainId:', currentChainId);
      debugLog('[MetaMaskButton] Expected chainId:', getCurrentNetwork().chainId);
      
      const code = await ethersProvider.getCode(CONTRACT_ADDRESS);
      debugLog('[MetaMaskButton] Contract code length:', code.length);
      debugLog('[MetaMaskButton] Contract code starts with:', code.substring(0, 66));
      
      if (code === '0x') {
        debugError('[MetaMaskButton] Contract does not exist at address:', CONTRACT_ADDRESS);
        debugError('[MetaMaskButton] This means either:');
        debugError('[MetaMaskButton] 1. Wrong network (not Sepolia)');
        debugError('[MetaMaskButton] 2. Wrong contract address');
        debugError('[MetaMaskButton] 3. MetaMask cache issue');
        onError?.(`Contract does not exist at address: ${CONTRACT_ADDRESS}. Please check the contract address and network.`);
        return;
      }
      debugLog('[MetaMaskButton] Contract code exists, length:', code.length);
    } catch (networkError) {
      debugError('[MetaMaskButton] Error checking contract on network:', networkError);
      onError?.('Failed to verify contract on current network.');
      return;
    }

    setIsLoading(true);
    setIsProcessing(true); // Block duplicate requests
    try {
      if (!provider || !address) throw new Error('MetaMask not connected');
      if (!CONTRACT_ADDRESS) throw new Error('Contract address not set');
      
      debugLog('[MetaMaskButton] handleMintOrEdit - hasMinted:', hasMinted, 'derivedHasMinted:', userHasMinted, 'tokenId:', tokenId);
      
      // Track attempt event
      if (userHasMinted) {
        await analytics.trackEditAttempt('metamask', tokenId || 0, currentCollections.length);
      } else {
        await analytics.trackMintAttempt('metamask', (mintPrice ?? DEFAULT_PRICE) as string, Boolean(isFreeMint), currentCollections.length);
      }
      
      const ethersProvider = new ethers.BrowserProvider(provider);
      const signer = await ethersProvider.getSigner();
      const contract = new ethers.Contract(CONTRACT_ADDRESS, NFTOTEM_ABI, signer);
      const signerAddrLower = (await signer.getAddress())?.toLowerCase?.();

      // Preflight safety: ensure signer address matches target wallet
      try {
        const signerAddr = signerAddrLower;
        const targetAddr = walletAddress?.toLowerCase?.();
        if (signerAddr && targetAddr && signerAddr !== targetAddr) {
          const signerShort = formatAddressForUi(signerAddr);
          const targetShort = formatAddressForUi(targetAddr);
          onError?.(`Different account selected in MetaMask. Connected: ${signerShort}. Target: ${targetShort}. Please switch account and try again.`);
          setIsWrongWallet(true);
          setShowReconnectPrompt(true);
          return;
        }
      } catch (addrErr) {
        debugWarn('[MetaMaskButton] Failed to read signer address before tx:', addrErr);
      }
      if (userHasMinted) {
        // EDIT FLOW
        if (!Array.isArray(currentCollections) || !currentCollections.length) {
          throw new Error('No collections for edit');
        }
        
        debugLog('[MetaMaskButton] Sending edit request with collections:', currentCollections.map(c => c.id));
        const res = await fetch('/api/edit-totem', {
        method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            walletAddress: signerAddrLower || walletAddress,
            collections: currentCollections.map(c => c.id)
          })
        });
        const data = await res.json();
        debugLog('[MetaMaskButton] Edit API response:', data);
        debugLog('[MetaMaskButton] Edit API response type:', typeof data);
        debugLog('[MetaMaskButton] Edit API response keys:', Object.keys(data));
        debugLog('[MetaMaskButton] Edit API response.success:', data.success);
        debugLog('[MetaMaskButton] Edit API response.data:', data.data);
        
        if (!data.success) throw new Error(data.error || 'Failed to get edit params');
        
        // Get tokenId from API response
        const editTokenId = data.data.tokenId;
        if (!editTokenId) {
          throw new Error('No tokenId received from edit API');
        }
        
        debugLog('[MetaMaskButton] Edit API success, proceeding to generate image...');
        debugLog('[MetaMaskButton] Data received:', JSON.stringify(data.data));
        debugLog('[MetaMaskButton] Using tokenId from API:', editTokenId);
        
        // Get new tokenURI via API
        debugLog('[MetaMaskButton] Calling /api/generate-totem-image...');
        const imageRes = await fetch('/api/generate-totem-image', {
        method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walletAddress,
            collections: currentCollections.map(c => c.id), // Pass only collection IDs
            tokenId: editTokenId,
            isEdit: true // CRITICAL: indicates this is editing
          })
        });
        debugLog('[MetaMaskButton] Image generation response status:', imageRes.status);
        const imageData = await imageRes.json();
        debugLog('[MetaMaskButton] Image generation response:', imageData);
        debugLog('[MetaMaskButton] Image generation response keys:', Object.keys(imageData));
        debugLog('[MetaMaskButton] Image generation response.data:', imageData.data);
        debugLog('[MetaMaskButton] Image generation response.data.metadata:', imageData.data?.metadata);
        debugLog('[MetaMaskButton] Image generation response.data.metadata keys:', Object.keys(imageData.data?.metadata || {}));
        debugLog('[MetaMaskButton] Image generation response.data.metadata?.external_url:', imageData.data?.metadata?.external_url);
        debugLog('[MetaMaskButton] Image generation response.data.metadata?.image:', imageData.data?.metadata?.image);
        
        // For Edit, we should use metadata URI, not image URI
        // This ensures Rarible and other marketplaces get full metadata with traits
        let newTokenURI = `https://nftotem.ilyakazakov.com/api/metadata/${tokenId || editTokenId}`;
        
        // Add version if available for cache busting
        if (imageData?.data?.metadata?.version) {
          newTokenURI += `?v=${imageData.data.metadata.version}`;
        }
        
        debugLog('[MetaMaskButton] Using tokenURI for edit:', newTokenURI);
        debugLog('[MetaMaskButton] TokenURI type:', typeof newTokenURI);
        debugLog('[MetaMaskButton] TokenURI length:', newTokenURI?.length);
        if (!newTokenURI) throw new Error('Failed to generate new tokenURI');
        
        // Validate URL format
        try {
          new URL(newTokenURI);
          debugLog('[MetaMaskButton] TokenURI is valid URL');
        } catch (e) {
          debugError('[MetaMaskButton] Invalid URL format:', newTokenURI);
          throw new Error('Invalid URL format for tokenURI');
        }
        
        debugLog('[MetaMaskButton] Editing totem with new tokenURI:', newTokenURI);
        const tx = await contract.edit(newTokenURI);
        debugLog('[MetaMaskButton] Edit transaction sent, waiting for confirmation...');
        await tx.wait();
        debugLog('[MetaMaskButton] Edit transaction confirmed!');
        
        // Track successful edit
        await analytics.trackEditTotem('metamask', tokenId || editTokenId, currentCollections.length);
        
        // Update KV record with new collections (simple approach)
        try {
          debugLog('[MetaMaskButton] Updating KV record with new collections');
          const updateRes = await fetch('/api/admin/update-token-25', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tokenId: tokenId || editTokenId,
              walletAddress: signerAddrLower || walletAddress,
              collections: currentCollections.map(c => ({
                id: c.id,
                name: c.name,
                cubeImage: getCubeImageForSnapshot(c.id)
              })),
              blockCount: currentCollections.length
            })
          });
          if (updateRes.ok) {
            debugLog('[MetaMaskButton] KV record updated successfully');
          } else {
            debugWarn('[MetaMaskButton] Failed to update KV record:', updateRes.status);
          }
        } catch (error) {
          debugWarn('[MetaMaskButton] Error updating KV record:', error);
        }
        
        // Show OpenSea link after successful edit
        try {
          const network = getCurrentNetwork();
          const openseaUrl = getOpenSeaUrl(network.contractAddress, (tokenId || editTokenId));
          toast({
            title: 'Totem edited successfully!',
            description: 'Your totem has been updated.',
            variant: 'success',
            action: (
              <ToastAction altText="View on OpenSea" onClick={() => window.open(openseaUrl, '_blank')}>
                View on OpenSea
              </ToastAction>
            )
          });
        } catch {
          toast({
            title: 'Totem edited successfully!',
            description: 'Your totem has been updated.',
            variant: 'success',
          });
        }
      } else {
        // Get tokenURI and price from backend
        debugLog('[MetaMaskButton] Sending mint request with collections:', currentCollections.map(c => c.id));
        const res = await fetch('/api/mint-totem', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ walletAddress: signerAddrLower || walletAddress, collections: currentCollections.map(c => c.id), prepare: true }),
        });
        const data = await res.json();
        debugLog('[MetaMaskButton] Mint API response:', data);
        if (!data.success) throw new Error(data.error || 'Failed to get mint params');
        const tokenURI = data.data.tokenURI || 'ipfs://placeholder-token-uri';
        const price: string = (data.data.price ?? DEFAULT_PRICE) as string;
        debugLog('[MetaMaskButton] Minting with price:', price, 'tokenURI:', tokenURI);
        const tx = await contract.mintTotem((signerAddrLower || walletAddress)!, { value: ethers.parseEther(price) });
        const receipt = await tx.wait();
        
        // Get tokenId from transaction receipt
        let tokenId = null;
        debugLog('[MetaMaskButton] Transaction receipt:', receipt);
        
        if (receipt && receipt.logs) {
          debugLog('[MetaMaskButton] Transaction logs count:', receipt.logs.length);
          
          // Try to extract tokenId from transaction receipt
          for (const log of receipt.logs) {
            try {
              debugLog('[MetaMaskButton] Processing log:', log);
              const parsed = contract.interface.parseLog(log);
              if (parsed && parsed.name === "Transfer") {
                tokenId = Number(parsed.args.tokenId);
                debugLog('[MetaMaskButton] Found tokenId in Transfer event:', tokenId);
                break;
              }
            } catch (e) {
              // not a Transfer event, skip
              debugLog('[MetaMaskButton] Log parsing failed (not Transfer event):', e);
            }
          }
          
          // If not found via parseLog, try direct extraction
          if (!tokenId) {
            debugLog('[MetaMaskButton] Trying direct extraction from logs');
            const TRANSFER_TOPIC = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';
            const contractAddress = CONTRACT_ADDRESS.toLowerCase();
            
            for (const log of receipt.logs) {
              if (log.address.toLowerCase() === contractAddress && 
                  log.topics && 
                  log.topics[0] === TRANSFER_TOPIC &&
                  log.topics.length >= 4) {
                try {
                  // topics[3] содержит tokenId
                  const tokenIdHex = log.topics[3];
                  tokenId = Number(BigInt(tokenIdHex));
                  debugLog('[MetaMaskButton] Found tokenId via direct extraction:', tokenId);
                  break;
                } catch (e) {
                  debugLog('[MetaMaskButton] Direct extraction failed:', e);
                }
              }
            }
          }
        }
        
        if (!tokenId) {
          debugWarn('[MetaMaskButton] Could not extract tokenId from transaction receipt');
          // Try to get tokenId via totalSupply
          try {
            debugLog('[MetaMaskButton] Trying to get tokenId via totalSupply');
            const totalSupply = await contract.totalSupply();
            // Contract numbers tokens from 1 → when totalSupply=N, last token = N
            tokenId = Number(totalSupply);
            debugLog('[MetaMaskButton] Got tokenId via totalSupply:', tokenId);
          } catch (e) {
            debugError('[MetaMaskButton] Failed to get tokenId via totalSupply:', e);
          }
        } else {
          debugLog('[MetaMaskButton] TokenId successfully extracted:', tokenId);
        }
        
        // Generate totem image and save ownership after successful mint
        debugLog('[MetaMaskButton] About to generate image and save ownership. TokenId:', tokenId, 'Type:', typeof tokenId);
        if (tokenId !== null && tokenId !== undefined) {
          debugLog('[MetaMaskButton] Proceeding with image generation and ownership creation');
          try {
            debugLog('[MetaMaskButton] Generating totem image for tokenId:', tokenId);
            debugLog('[MetaMaskButton] Current collections:', currentCollections);
            debugLog('[MetaMaskButton] Collections IDs:', currentCollections.map(c => c.id));
            const imageRes = await fetch('/api/generate-totem-image', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                walletAddress,
                collections: currentCollections.map(c => c.id),
                tokenId: tokenId,
                isEdit: false // Indicates this is minting, not editing
              })
            });
            
            let imageUrl = tokenURI; // Fallback to tokenURI
            if (imageRes.ok) {
              const imageData = await imageRes.json();
              imageUrl = imageData?.data?.metadata?.image || imageData?.data?.metadata?.external_url || tokenURI;
              debugLog('[MetaMaskButton] Generated totem image URL:', imageUrl);
            } else {
              debugError('[MetaMaskButton] Failed to generate totem image:', imageRes.status, imageRes.statusText);
              const errorData = await imageRes.text();
              debugError('[MetaMaskButton] Image generation error details:', errorData);
            }
            
            // Use reliable persistence with retry mechanism
            await persistTotemDataWithRetry(signerAddrLower || walletAddress, tokenId, currentCollections);
          } catch (error) {
            debugWarn('Error saving totem ownership:', error);
          }
        }
        
      // Track successful mint
      if (tokenId !== null && tokenId !== undefined) {
          if (isFreeMint) {
            await analytics.trackFreeMint('metamask', currentCollections.length, tokenId);
          } else {
            await analytics.trackPaidMint('metamask', (mintPrice ?? DEFAULT_PRICE) as string, currentCollections.length, tokenId);
          }
        }
        
      // Generate OpenSea URL
      const network = getCurrentNetwork()
      const openseaUrl = tokenId ? getOpenSeaUrl(network.contractAddress, tokenId) : null
      
      toast({
          title: 'Totem minted successfully!',
          description: `Your totem has been minted with token ID #${tokenId}.`,
          variant: 'success',
          action: openseaUrl ? (
            <ToastAction altText="View on OpenSea" onClick={() => window.open(openseaUrl, '_blank')}>
              View on OpenSea
            </ToastAction>
          ) : undefined
        });
      }
      setHasMinted(true);
    } catch (e: any) {
      debugError('[MetaMaskButton] Error in handleMintOrEdit:', e);
      debugError('[MetaMaskButton] Error details:', {
        message: e?.message,
        code: e?.code,
        stack: e?.stack
      });
      
      // Show user-friendly error messages and pass to parent
      let errorMessage = 'Minting failed. Please try again.';
      
      // Debug logging for error parsing
      debugLog('[MetaMaskButton] Error parsing debug:', {
        code: e?.code,
        message: e?.message,
        reason: e?.reason,
        data: e?.data
      });
      
      if (e?.code === 4001 || /user denied|user rejected/i.test(e?.message)) {
        errorMessage = 'Transaction was cancelled by user.';
      } else if (e?.code === 'INSUFFICIENT_FUNDS' || /insufficient funds/i.test(e?.message)) {
        errorMessage = 'Insufficient funds for gas fee. Please ensure you have enough ETH.';
      } else if (e?.code === 'UNPREDICTABLE_GAS_LIMIT' || /gas estimation failed/i.test(e?.message)) {
        errorMessage = 'Gas estimation failed. Please try again or check your wallet balance.';
      } else if (e?.code === 'CALL_EXCEPTION' || /execution reverted/i.test(e?.message)) {
        // Check for specific contract errors
        if (/only owner/i.test(e?.message) || /only owner/i.test(e?.reason)) {
          errorMessage = 'This totem belongs to another wallet. You can only mint totems for wallets you own.';
        } else if (/cannot mint totem for another wallet/i.test(e?.message) || /cannot mint totem for another wallet/i.test(e?.reason)) {
          // Differentiate between two user scenarios
          const isDifferentWallet = typeof address === 'string' && typeof walletAddress === 'string'
            && address.toLowerCase() !== walletAddress.toLowerCase();
          if (isDifferentWallet) {
            const truncated = formatAddressForUi(walletAddress);
            errorMessage = `You can’t mint a totem for another wallet. Connect wallet ${truncated} in MetaMask or change the target wallet on the page.`;
          } else {
            errorMessage = 'You can’t mint a totem for another wallet. Please reconnect MetaMask to the correct account and try again.';
          }
        } else if (/not the owner of this totem/i.test(e?.message) || /not the owner of this totem/i.test(e?.reason)) {
          errorMessage = 'This totem belongs to another wallet. You can only mint totems for wallets you own.';
        } else if (/already minted/i.test(e?.message) || /already minted/i.test(e?.reason)) {
          errorMessage = 'This wallet has already minted a totem.';
        } else if (/minting disabled/i.test(e?.message) || /minting disabled/i.test(e?.reason)) {
          errorMessage = 'Minting is currently disabled.';
        } else if (/no totem found for this wallet/i.test(e?.message) || /no totem found for this wallet/i.test(e?.reason)) {
          errorMessage = 'No totem found for this wallet. Please mint a totem first.';
        } else if (/free mint requires no payment/i.test(e?.message) || /free mint requires no payment/i.test(e?.reason)) {
          errorMessage = 'This wallet is eligible for free minting. Please use the free mint option.';
        } else if (/insufficient payment/i.test(e?.message) || /insufficient payment/i.test(e?.reason)) {
          errorMessage = 'Insufficient payment. Please send the correct amount of ETH.';
        } else {
          errorMessage = 'Transaction failed. Please check your wallet and try again.';
        }
      } else if (e?.code === 'NETWORK_ERROR' || /network error/i.test(e?.message)) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      debugError('[MetaMaskButton] User error message:', errorMessage);
      
      // Track error event
      if (userHasMinted) {
        await analytics.trackEditError('metamask', errorMessage, tokenId || undefined);
      } else {
        await analytics.trackMintError('metamask', errorMessage, (mintPrice ?? DEFAULT_PRICE) as string, Boolean(isFreeMint));
      }
      
      onError?.(errorMessage);
    } finally {
      debugLog('[MetaMaskButton] handleMintOrEdit finally block - cleaning up...');
      setIsLoading(false);
      setIsProcessing(false); // Release lock after check completion
      debugLog('[MetaMaskButton] handleMintOrEdit completed');
    }
  };

  // Determine button text with smooth transitions
  const getButtonText = () => {
    if (isLoading) {
      return userHasMinted ? 'Editing...' : 'Minting...';
    }
    // Until we get eligibility/price, show neutral text
    if (isFreeMint === null || mintPrice === null) {
      return userHasMinted ? 'Edit Totem' : 'Mint Totem';
    }
    if (isWrongWallet) {
      return 'Wrong wallet connected';
    }
    if (userHasMinted) {
      return 'Edit Totem';
    }
    if (isFreeMint) {
      return 'Mint Totem (Free)';
    }
    return `Mint Totem (${mintPrice as string} ETH)`;
  };

  // Determine if loader should be shown
  const showLoader = isLoading || connecting || (isWrongNetwork && !autoSwitchFailed);
  // Allow click on wrong network to trigger automatic switch
  const isButtonDisabled = isLoading || isWrongWallet || isProcessing;

  // Only show Connect MetaMask when user is not connected at all, not when viewing someone else's totem
  const forceConnectUi = metaMaskAvailable && !address;

  // Check if user has any collections
  const hasCollections = currentCollections && currentCollections.length > 0;

  // If wallet is connected and we're viewing someone else's totem, show appropriate button
  if (address && type === 'metamask' && metaMaskAvailable && !forceConnectUi) {
    // If user has no collections, show informational message instead of button
    if (!hasCollections) {
      return (
        <div className="w-full p-4 bg-gray-100 rounded-lg border border-gray-200 text-center">
          <p className="text-sm text-gray-600 mb-2">
            No NFT collections found in this wallet
          </p>
          <p className="text-xs text-gray-500">
            You need to own at least one NFT from the selected collections to mint a totem.
          </p>
        </div>
      );
    }

    return (
      <Button 
        onClick={handleMintOrEdit} 
        className={`${className} transition-all duration-300 ease-in-out relative overflow-hidden`} 
        disabled={isButtonDisabled}
      >
        {showLoader && (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        )}
        <span className={`transition-opacity duration-200 font-funnel text-medium uppercase ${showLoader ? 'opacity-90' : 'opacity-100'}`}>
          {isWrongNetwork
            ? (autoSwitchFailed ? `Switch to ${getCurrentNetwork().name}` : 'Switching network...')
            : getButtonText()}
        </span>
      </Button>
    );
  }

  // Show Connect MetaMask (either not connected, or forcing reconnect prompt after mismatch)
  return (
    <Button
      onClick={metaMaskAvailable ? handleConnectOrSwitch : undefined} 
      className={`${className} transition-all duration-300 ease-in-out relative overflow-hidden`} 
      disabled={connecting || !metaMaskAvailable}
    >
      {connecting && (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      )}
      <span className={`transition-opacity duration-200 font-funnel uppercase ${connecting ? 'opacity-90' : 'opacity-100'}`}>
        {metaMaskAvailable ? (connecting ? 'Connecting...' : 'Connect MetaMask') : 'Install MetaMask'}
      </span>
    </Button>
  );
} 